import { useEffect, useRef, useState } from 'react';

import { ErrorApi } from '@haus/core';

import { Boton } from '../componentes/Boton.js';
import { Hoja, estilosHoja } from '../componentes/Hoja.js';
import { Aviso, BarraAccion, Pantalla } from '../componentes/estructura.js';
import { useCamara } from '../captura/useCamara.js';
import { useGps } from '../captura/useGps.js';
import { empezarLimpieza, sincronizarLimpieza } from '../datos/limpiezas.js';
import { limpiezaAbierta } from '../datos/db.js';
import { useApp } from '../estado/app.js';
import css from './comun.module.css';

/**
 * 03 · Escanear el QR del apartamento.
 *
 * UNA DECISIÓN QUE AHORRA UNA DEPENDENCIA Y FUNCIONA MEJOR: el QR contiene una
 * URL (`https://…/c/<qr_token>`), no un código propietario. Eso significa que
 * **la cámara del propio teléfono ya sabe abrirlo**, y al abrirlo entra en esta
 * app con el apartamento resuelto.
 *
 * Así que aquí solo se escanea de verdad donde el navegador trae
 * `BarcodeDetector` —Chrome en Android—, y donde no lo trae (Safari) se dice
 * lo que hay que hacer en lugar de meter un lector de QR en JavaScript de
 * 40 kB que además va lento en un móvil modesto.
 *
 * El campo manual está siempre: es lo que salva el día cuando la etiqueta está
 * rayada o hay poca luz.
 */
export function PantallaQr() {
    const { t, cliente, ir, atras, dia } = useApp();
    const { pedir } = useGps();
    const camara = useCamara('480p', false);
    const video = useRef<HTMLVideoElement>(null);
    const [manual, setManual] = useState(false);
    const [codigo, setCodigo] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);

    const puedeEscanear = typeof (globalThis as { BarcodeDetector?: unknown }).BarcodeDetector === 'function';

    useEffect(() => {
        if (puedeEscanear) {
            void camara.encender();
        }
        // camara.encender es estable (useCallback) y encenderla dos veces no
        // hace nada, pero incluirla aquí provocaría un ciclo de renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [puedeEscanear]);

    useEffect(() => {
        if (camara.stream !== null && video.current !== null) {
            video.current.srcObject = camara.stream;
        }
    }, [camara.stream]);

    // Bucle de detección. Cada 400 ms: suficiente para que se sienta
    // inmediato y poco para que no caliente el móvil.
    useEffect(() => {
        if (!puedeEscanear || camara.stream === null || ocupado) {
            return undefined;
        }

        const Detector = (globalThis as unknown as {
            BarcodeDetector: new (o: { formats: string[] }) => {
                detect(v: HTMLVideoElement): Promise<{ rawValue: string }[]>;
            };
        }).BarcodeDetector;
        const detector = new Detector({ formats: ['qr_code'] });

        const id = setInterval(() => {
            void (async () => {
                if (video.current === null) {
                    return;
                }
                try {
                    const codigos = await detector.detect(video.current);
                    const valor = codigos[0]?.rawValue;
                    if (valor !== undefined) {
                        const m = /\/c\/([a-z0-9]{32})/.exec(valor);
                        if (m?.[1] !== undefined) {
                            await abrir(m[1]);
                        }
                    }
                } catch {
                    /* un fotograma que no se puede leer no es un error */
                }
            })();
        }, 400);

        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [puedeEscanear, camara.stream, ocupado]);

    async function abrir(qrToken: string): Promise<void> {
        if (ocupado) {
            return;
        }
        setOcupado(true);
        setError(null);

        try {
            const r = await cliente.resolverQr(qrToken);

            // Si ya hay una limpieza abierta de este apartamento, se retoma en
            // lugar de empezar otra: escanear el QR dos veces es lo normal
            // cuando se sale y se vuelve a entrar.
            const abierta = await limpiezaAbierta();
            if (abierta !== null && abierta.property_id === r.property_id) {
                camara.apagar();
                // 'reemplaza': el escáner desaparece de la pila. Volver atrás
                // desde el recorrido tiene que llevar al día, no a una cámara
                // apuntando a un QR ya leído.
                ir({ n: 'recorrido', limpieza: abierta.id }, 'reemplaza');

                return;
            }

            const asignacion = dia?.asignaciones.find((a) => a.apartamento.id === r.property_id);
            if (asignacion === undefined) {
                // El servidor dice que sí es suyo, pero el día cacheado no lo
                // tiene: pasa si oficina lo asignó hace un minuto. Se recarga
                // el día desde la lista, que es donde se puede reintentar.
                setError(t('hoy.vacio_ayuda'));

                return;
            }

            const gps = await pedir();
            const modo = asignacion.apartamento.modo_captura === 'fotos' ? 'fotos' : 'video';
            const l = await empezarLimpieza(asignacion, modo, gps);
            void sincronizarLimpieza(cliente, l);
            camara.apagar();
            ir({ n: 'recorrido', limpieza: l.id }, 'reemplaza');
        } catch (e) {
            if (e instanceof ErrorApi && e.codigo === 'apartamento_no_asignado_hoy') {
                const nombre = typeof e.extra['apartamento'] === 'string' ? e.extra['apartamento'] : '';
                setError(t('qr.no_asignado_ayuda', { apartamento: nombre }));
            } else if (e instanceof ErrorApi && e.codigo === 'sin_red') {
                setError(t('error.sin_red_ayuda'));
            } else {
                setError(t('qr.no_asignado'));
            }
        } finally {
            setOcupado(false);
        }
    }

    return (
        <Pantalla fondo="oscuro">
            <header
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--e12)',
                    padding: 'var(--e14) var(--e18)',
                    paddingTop: 'max(var(--e14), env(safe-area-inset-top))',
                }}
            >
                <button
                    className={css.botonRedondo}
                    style={{ width: 40, height: 40, minWidth: 40 }}
                    onClick={atras}
                    aria-label={t('comun.atras')}
                >
                    ←
                </button>
                <span style={{ fontSize: 'var(--txt-cuerpo)', fontWeight: 600, color: '#f2ede6' }}>
                    {t('qr.titulo')}
                </span>
            </header>

            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--e18)',
                    padding: 'var(--e20)',
                    textAlign: 'center',
                }}
            >
                {puedeEscanear ? (
                    <div
                        style={{
                            width: 240,
                            height: 240,
                            borderRadius: 28,
                            border: '3px solid rgba(255,255,255,.25)',
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        <video
                            ref={video}
                            playsInline
                            muted
                            autoPlay
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </div>
                ) : (
                    <div
                        style={{
                            width: 240,
                            height: 240,
                            borderRadius: 28,
                            border: '3px dashed rgba(255,255,255,.25)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 46,
                        }}
                    >
                        ▣
                    </div>
                )}

                <p style={{ color: 'rgba(255,255,255,.72)', maxWidth: 280, fontSize: 'var(--txt-cuerpo)' }}>
                    {puedeEscanear ? t('qr.ayuda') : t('qr.camara_sistema_ayuda')}
                </p>

                {ocupado && <span className="mono" style={{ color: 'rgba(255,255,255,.6)' }}>{t('qr.escaneando')}</span>}

                {error !== null && <Aviso>{error}</Aviso>}

                {camara.estado === 'denegada' && <Aviso>{t('qr.sin_permiso')}</Aviso>}
            </div>

            <BarraAccion>
                <Boton variante="secundario" onClick={() => setManual(true)}>
                    {t('qr.manual')}
                </Boton>
            </BarraAccion>

            {manual && (
                <Hoja titulo={t('qr.manual_titulo')} ayuda={t('qr.manual_ayuda')} alCerrar={() => setManual(false)}>
                    <input
                        className={`${estilosHoja.campo} ${estilosHoja.campoLinea} mono`}
                        value={codigo}
                        inputMode="text"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        maxLength={32}
                        onChange={(e) => setCodigo(e.target.value.trim().toLowerCase())}
                    />
                    <Boton
                        disabled={codigo.length !== 32 || ocupado}
                        onClick={() => {
                            setManual(false);
                            void abrir(codigo);
                        }}
                    >
                        {t('comun.aceptar')}
                    </Boton>
                </Hoja>
            )}
        </Pantalla>
    );
}
